<template>
  <div
    class="video-player"
    :class="blockClass"
  >
    <transition name="fade">
      <button
        v-if="showOverlay"
        class="video-player__overlay"
        type="button"
        @click="handleOverlayClick"
      >
        <responsive-image
          v-if="showOverlay && image.src"
          class="video-player__image"
          :image="image"
          min-max="300-1000"
          :padding="false"
        />
      </button>
    </transition>

    <div class="video-player__controls">
      <button
        class="video-player__control"
        tabindex="-1"
        type="button"
        @click="handleControlClick"
      >
        <span
          class="visually-hidden"
          v-text="controlText"
        />

        <component :is="icon" />
      </button>

      <button
        v-if="subtitles"
        class="video-player__subtitles"
        :class="{ [$class('active')]: displaySubtitles }"
        type="button"
        @click="displaySubtitles = !displaySubtitles"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.video.subtitles')"
        />

        <span
          aria-hidden="true"
          v-text="$string('accessibility.video.cc')"
        />
      </button>
    </div>

    <iframe
      v-if="video.url.includes('youtube')"
      :src="`${video.url}&loop=1&controls=0&rel=0&muted=1`"
      :title="$string('accessibility.video.youtube')"
      frameborder="0"
      allow=" loop; encrypted-media; picture-in-picture"
      allowfullscreen
    ></iframe>

    <iframe
        v-if="video.url.includes('vimeo')"
        :src="`${video.url}?h=c0878e73c7&color=ef9700&title=0&byline=0&portrait=0&loop=1&controls=0&muted=1`" 
        :title="$string('accessibility.video.vimeo')"
        frameborder="0"
        allow=" loop; fullscreen; picture-in-picture"
        allowfullscreen
    ></iframe>

    <video
      v-if="!video.url.includes('vimeo') && !video.url.includes('youtube')"
      ref="video"
      class="video-player__video"
      controlslist="nodownload noremoteplayback"
      crossorigin="anonymous"
      disablepictureinpicture
      :controls="controls ? true : false"
      :loop="loop ? true : false"
      :muted="muted ? true : false"
      playsinline
      :aria-label="controlText"
      @canplay="state = 'ready'"
      @canplaythrough="state = 'ready'"
      @click="handleControlClick"
      @keyup.enter="handleControlClick"
      @loadedmetadata="state = 'ready'"
      @pause="state = 'paused'"
      @play="state = 'playing'"
      @timeupdate="handleVideoTimeUpdate"
    >
      <source
        :src="video.url"
        :type="video.mimeType"
      >

      <track
        v-if="subtitles"
        kind="subtitles"
        label="English"
        :src="subtitles"
        srclang="en"
      >
    </video>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Lookbook video player functionality.
 *
 * @param {Boolean} [isActive] - Slide containing video is active.
 * @param {Object} [image] - Shopify image object.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {String} video - Video URL.
 *
 */
import { mapGetters } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import IconLoading from '~icons/general/loading.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconLoading,
    IconPause,
    IconPlay,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
      required: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    subtitles: {
      type: String,
      default: '',
    },
    video: {
      type: Object,
      default: () => ({
        format: '',
        mimeType: '',
        url: '',
      }),
      required: true,
    },
  },

  data() {
    return {
      displaySubtitles: false,
      overlayPaused: false,
      showOverlay: true,
      state: 'loading',
      time: 0,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.state) {
        case 'loading':
          return this.$string('accessibility.video.loading')

        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    icon() {
      switch (this.state) {
        case 'loading':
          return 'icon-loading'

        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },
  },

  watch: {

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.$refs.video) {
        return
      }

      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },

    /**
     * Watch active overlay to pause/resume (if already playing).
     */
    getActiveOverlay() {
      if (this.getActiveOverlay) {
        if (this.state === 'playing') {
          this.pauseVideo()
          this.overlayPaused = true
          return
        }

        this.overlayPaused = false
        return
      }

      /**
       * When overlay closes if video was previously playing resume it.
       */
      if (this.overlayPaused) {
        this.playVideo()
      }
    },

    /**
     * Watch active state to play video when slide containing it becomes active.
     * - Don't autoplay if prefers reduced motion.
     */
    isActive() {
      this.handleAutoplay()
    },
  },

  mounted() {
    if (!this.$refs.video) {
      this.showOverlay = false
      return
    }

    /**
     * Reset any subtitles that browser has enabled by default.
     */
    if (!this.$refs.video.textTracks.length) {
      Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
        this.$refs.video.textTracks[index].mode = 'hidden'
      })
    }

    /**
     * Check isActive state on mounted.
     */
    this.handleAutoplay()
  },

  methods: {

    /**
     * Handle autoplay.
     */
    handleAutoplay() {
      if (
        this.isActive &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.playVideo()
        return
      }

      this.pauseVideo()
    },

    /**
     * Handle overlay click.
     */
    handleOverlayClick() {
      if (this.state === 'loading') {
        return
      }

      this.playVideo()
    },

    /**
     * Handle control and video click.
     */
    handleControlClick() {
      if (this.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Play video.
     */
    async playVideo() {
      if (!this.$refs.video) {
        return
      }

      try {
        await this.$refs.video.play()
        this.showOverlay = false

      } catch (error) {
        cnvs.ReportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      if (!this.$refs.video) {
        return
      }

      try {
        await this.$refs.video.pause()

      } catch (error) {
        cnvs.ReportError('Failed to pause video', error)
      }
    },

    /**
     * Handle time update on video.
     */
    handleVideoTimeUpdate() {
      if (this.$refs.video) {
        return
      }

      this.time = this.$refs.video?.currentTime || 0
      this.state = 'playing'
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
